import React, { useMemo } from 'react'
import { Route, Routes } from 'react-router-dom'
import { YStack, XStack } from 'tamagui'
import { lazy as loadable } from '@loadable/component'
import { useScript } from 'hoofd'
import { Trans } from '@lingui/react/macro'

import { useScreenMessages } from '@data/i18n'
import {
  buildBreadcrumbList,
  HOME_BREADCRUMB,
} from '@newComponents/buildBreadcrumbList'
import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import type { GuildIconKeys } from '@newComponents/GuildIcon/GuildIcon'
import { Link } from '@newComponents/Link'
import { Metadata } from '@newComponents/Metadata'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { wrapSuspenseComponent } from '@newComponents/SuspenseWrapper'
import { H1, H3, Paragraph } from '@newComponents/Text'
import { HOST_NAME } from '@data/config/url'

import { useDocSections } from './shared/docRoutes'

// Lazy-loaded section components
const DocsGettingStartedSection = wrapSuspenseComponent(
  loadable(() => import('./getting-started/GettingStartedSection'))
)
const DocsGuildsSection = wrapSuspenseComponent(
  loadable(() => import('./guilds/GuildsSection'))
)
const DocsEventsSection = wrapSuspenseComponent(
  loadable(() => import('./events/EventsSection'))
)
const DocsPresentationsSection = wrapSuspenseComponent(
  loadable(() => import('./presentations/PresentationsSection'))
)
const DocsAccountSection = wrapSuspenseComponent(
  loadable(() => import('./account/AccountSection'))
)
const DocsDevelopersSection = wrapSuspenseComponent(
  loadable(() => import('./developers/DevelopersSection'))
)

const loadDocsScreenCatalog = (locale: string) =>
  import(
    /* webpackChunkName: "locale-docs-screen-[request]" */ `./locales/${locale}.po`
  )

// --- Landing page components ---

const DocCard = ({
  title,
  description,
  href,
  icon,
}: {
  title: string
  description: string
  href: string
  icon: GuildIconKeys
}) => (
  <Link href={href} hoverStyle={{ textDecorationLine: 'none' }}>
    <Card
      padding='$5'
      gap='$3'
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
    >
      <XStack alignItems='center' gap='$3'>
        <Icon name={icon} size='$5' color='$colorAccent' />
        <H3>{title}</H3>
      </XStack>
      <Paragraph size='$5' color='$colorMuted'>
        {description}
      </Paragraph>
    </Card>
  </Link>
)

const DocsLandingPage = () => {
  const docSections = useDocSections()

  const breadcrumbs = useMemo(
    () =>
      buildBreadcrumbList([
        HOME_BREADCRUMB,
        { name: 'Documentation', url: `${HOST_NAME}/docs` },
      ]),
    []
  )
  useScript({ type: 'application/ld+json', text: breadcrumbs })

  const collectionPage = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Guild Documentation',
        description:
          'Learn how to use Guild to manage your communities, Events, and Presentations.',
        url: `${HOST_NAME}/docs`,
        hasPart: docSections.map((section) => ({
          '@type': 'WebPage',
          name: section.label,
          url: `${HOST_NAME}/docs/${section.path}`,
        })),
      }),
    [docSections]
  )
  useScript({ type: 'application/ld+json', text: collectionPage })

  return (
    <>
      <Metadata
        title='Documentation'
        description='Learn how to use Guild to manage your communities, Events, and Presentations.'
        canonicalUrl={`${HOST_NAME}/docs`}
      />
      <GradientBackgroundScreen alignItems='center'>
        <YStack gap='$10' maxWidth={720} width='100%'>
          <YStack gap='$3'>
            <H1>
              <Trans>Documentation</Trans>
            </H1>
            <Paragraph size='$6' color='$colorMuted'>
              <Trans>
                Everything you need to know about creating and managing Guilds,
                Events, and Presentations on Guild.
              </Trans>
            </Paragraph>
          </YStack>

          <YStack tag='section' gap='$4'>
            {docSections.map((section) => (
              <DocCard
                key={section.path}
                title={section.label}
                description={section.description}
                href={`/docs/${section.path}`}
                icon={section.icon}
              />
            ))}
          </YStack>
        </YStack>
      </GradientBackgroundScreen>
    </>
  )
}

// --- Router ---

export const DocsScreen = () => {
  useScreenMessages(loadDocsScreenCatalog)

  return (
    <Routes>
      <Route index element={<DocsLandingPage />} />
      <Route path='getting-started/*' element={<DocsGettingStartedSection />} />
      <Route path='guilds/*' element={<DocsGuildsSection />} />
      <Route path='events/*' element={<DocsEventsSection />} />
      <Route path='presentations/*' element={<DocsPresentationsSection />} />
      <Route path='account/*' element={<DocsAccountSection />} />
      <Route path='developers/*' element={<DocsDevelopersSection />} />
    </Routes>
  )
}

export default DocsScreen
